/**
 * In-page Power BI-style matrix visual for the /calendar feed.
 *
 * Rows: districts that have at least one event in the filtered window.
 * Columns: every day between min and max event date (snapped to whole-month
 *          boundaries). Weekend columns are visually shaded.
 * Cells:  short type-code(s) for that (district, date), colored by the
 *          first type's chip color. Title attribute spells out full names.
 *
 * Sticky behavior keeps the district name visible while scrolling
 * horizontally, and the month/day header rows visible while scrolling
 * vertically. The two thead rows have explicit heights so the second
 * row can `top: 32px` itself below the first.
 */

import { useMemo } from 'react';

import type { CalendarFeedItem } from '../../api/calendar';
import {
  TYPE_ABBR,
  TYPE_BADGE_CLASSNAMES,
  TYPE_LABELS,
} from './CalendarMonthGrid';

interface CalendarMatrixViewProps {
  events: CalendarFeedItem[];
  /** Optional explicit lower bound. If omitted, snaps to first-of-month
   *  of earliest event. */
  fromDate?: Date;
  /** Optional explicit upper bound. If omitted, snaps to last-of-month
   *  of latest event. */
  toDate?: Date;
}

const TYPE_ORDER = [
  'Inservice',
  'Clerical',
  'ProfessionalDevelopment',
  'Closed',
  'Graduation',
  'StudentFirstDay',
  'StudentLastDay',
  'TeacherFirstDay',
  'TeacherLastDay',
];

function isoOf(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromIsoLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isWeekend(d: Date): boolean {
  const wd = d.getDay();
  return wd === 0 || wd === 6;
}

interface MonthSpan {
  label: string;
  cols: number;
}

export const CalendarMatrixView: React.FC<CalendarMatrixViewProps> = ({
  events,
  fromDate,
  toDate,
}) => {
  const { allDates, orgs, cellCodes, monthSpans } = useMemo(() => {
    if (events.length === 0) {
      return {
        allDates: [] as Date[],
        orgs: [] as Array<{ id: number; name: string }>,
        cellCodes: new Map<string, string[]>(),
        monthSpans: [] as MonthSpan[],
      };
    }

    const eventDates = events.map((e) => fromIsoLocal(e.calendarDate));
    const derivedMin = new Date(Math.min(...eventDates.map((d) => d.getTime())));
    const derivedMax = new Date(Math.max(...eventDates.map((d) => d.getTime())));

    const minDate =
      fromDate ?? new Date(derivedMin.getFullYear(), derivedMin.getMonth(), 1);
    const maxDate =
      toDate ?? new Date(derivedMax.getFullYear(), derivedMax.getMonth() + 1, 0);

    const dates: Date[] = [];
    const cursor = new Date(minDate);
    while (cursor <= maxDate) {
      dates.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    // Districts + cell codes
    const orgMap = new Map<number, { id: number; name: string }>();
    const codes = new Map<string, string[]>(); // key: `${orgId}|${iso}`
    for (const ev of events) {
      const name =
        ev.organizationShortName ?? ev.organizationName ?? `#${ev.educationOrganizationId}`;
      orgMap.set(ev.educationOrganizationId, { id: ev.educationOrganizationId, name });
      const key = `${ev.educationOrganizationId}|${ev.calendarDate}`;
      const arr = codes.get(key) ?? [];
      arr.push(ev.specialDayTypeCode ?? 'Unknown');
      codes.set(key, arr);
    }
    const sortedOrgs = Array.from(orgMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    // Month spans for header row 1
    const spans: MonthSpan[] = [];
    if (dates.length > 0) {
      let groupStart = 0;
      let groupKey = `${dates[0].getFullYear()}-${dates[0].getMonth()}`;
      let groupLabel = dates[0].toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric',
      });
      for (let i = 1; i < dates.length; i++) {
        const k = `${dates[i].getFullYear()}-${dates[i].getMonth()}`;
        if (k !== groupKey) {
          spans.push({ label: groupLabel, cols: i - groupStart });
          groupStart = i;
          groupKey = k;
          groupLabel = dates[i].toLocaleDateString(undefined, {
            month: 'short',
            year: 'numeric',
          });
        }
      }
      spans.push({ label: groupLabel, cols: dates.length - groupStart });
    }

    return {
      allDates: dates,
      orgs: sortedOrgs,
      cellCodes: codes,
      monthSpans: spans,
    };
  }, [events, fromDate, toDate]);

  if (events.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No events to display.
      </p>
    );
  }

  return (
    <div
      className="overflow-auto border border-gray-200 dark:border-gray-700 rounded max-h-[640px]"
      role="region"
      aria-label="Calendar matrix"
    >
      <table className="text-xs border-collapse">
        <thead>
          {/* Row 1: month spans */}
          <tr>
            <th
              className="sticky top-0 left-0 z-30 bg-gray-100 dark:bg-gray-700 border-b border-r border-gray-300 dark:border-gray-600 px-3 py-1.5 text-left text-gray-700 dark:text-gray-200 font-semibold whitespace-nowrap"
              style={{ minWidth: 200 }}
            >
              District
            </th>
            {monthSpans.map((span, i) => (
              <th
                key={i}
                colSpan={span.cols}
                className="sticky top-0 z-20 bg-gray-100 dark:bg-gray-700 border-b border-l border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center font-semibold text-gray-700 dark:text-gray-200"
              >
                {span.label}
              </th>
            ))}
          </tr>
          {/* Row 2: day numbers */}
          <tr>
            <th
              className="sticky left-0 z-30 bg-gray-50 dark:bg-gray-800 border-b border-r border-gray-200 dark:border-gray-700 px-3 py-1 text-left text-gray-500 dark:text-gray-400"
              style={{ top: 32 }}
            />
            {allDates.map((d, i) => (
              <th
                key={i}
                className={`sticky z-20 border-b border-gray-200 dark:border-gray-700 px-0.5 py-1 text-center font-medium w-7 ${
                  isWeekend(d)
                    ? 'bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-400'
                    : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                style={{ top: 32 }}
              >
                {d.getDate()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orgs.map((org) => (
            <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
              <th
                scope="row"
                className="sticky left-0 z-10 bg-white dark:bg-gray-800 border-b border-r border-gray-200 dark:border-gray-700 px-3 py-1 text-left font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap"
                style={{ minWidth: 200 }}
              >
                {org.name}
              </th>
              {allDates.map((d, i) => {
                const iso = isoOf(d);
                const wknd = isWeekend(d);
                const cellKey = `${org.id}|${iso}`;
                const cellCodesForCell = cellCodes.get(cellKey);

                if (cellCodesForCell && cellCodesForCell.length > 0) {
                  const sortedCodes = [...cellCodesForCell].sort((a, b) => {
                    const aIx = TYPE_ORDER.indexOf(a);
                    const bIx = TYPE_ORDER.indexOf(b);
                    return (aIx === -1 ? 99 : aIx) - (bIx === -1 ? 99 : bIx);
                  });
                  const primary = sortedCodes[0];
                  const colorCls =
                    TYPE_BADGE_CLASSNAMES[primary] ??
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
                  const display = sortedCodes
                    .map((c) => TYPE_ABBR[c] ?? c.charAt(0))
                    .join('/');
                  const tooltip = sortedCodes
                    .map((c) => TYPE_LABELS[c] ?? c)
                    .join(', ');
                  return (
                    <td
                      key={i}
                      title={tooltip}
                      className={`text-center px-0.5 py-1 border-b border-gray-100 dark:border-gray-800 ${colorCls} font-semibold`}
                    >
                      {display}
                    </td>
                  );
                }

                return (
                  <td
                    key={i}
                    className={`text-center px-0.5 py-1 border-b border-gray-100 dark:border-gray-800 ${
                      wknd ? 'bg-gray-100 dark:bg-gray-900' : ''
                    }`}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
